import type { EffectsValue } from '@unifyapps/defs/types/effectObject';
import { type CSSProperties, useMemo } from 'react';
import {
  getBackgroundBlurPx,
  getEffectsStyle,
  getInnerShadowOverlayStyle,
} from './getEffectsStyle';

export type StackEffectsWeb = {
  effectsStyle: CSSProperties;
  hasBackgroundBlur: boolean;
  hasInnerShadowOverlay: boolean;
  backgroundBlurOverlayStyle: CSSProperties | undefined;
  innerShadowOverlayStyle: CSSProperties | undefined;
};

// Bg-blur overlay paints behind static children — negative z-index plus a
// stacking context on the parent (set in the caller via `mergeStackingContext`).
const buildBgBlurOverlay = (backgroundBlurPx: number): CSSProperties | undefined => {
  if (backgroundBlurPx <= 0) return undefined;
  return {
    position: 'absolute',
    inset: 0,
    zIndex: -1,
    pointerEvents: 'none',
    backdropFilter: `blur(${backgroundBlurPx}px)`,
    WebkitBackdropFilter: `blur(${backgroundBlurPx}px)`,
    borderRadius: 'inherit',
  };
};

// Inner-shadow overlay paints above static children — positive z-index, no
// stacking context required so absolute children can still escape the Stack.
const buildInnerShadowOverlay = (
  innerShadowBoxShadow: CSSProperties | undefined,
): CSSProperties | undefined => {
  if (!innerShadowBoxShadow) return undefined;
  return {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    pointerEvents: 'none',
    borderRadius: 'inherit',
    ...innerShadowBoxShadow,
  };
};

export const useStackEffects = (effects: EffectsValue | undefined): StackEffectsWeb => {
  const effectsStyle = useMemo(() => getEffectsStyle(effects), [effects]);
  const backgroundBlurPx = useMemo(() => getBackgroundBlurPx(effects), [effects]);
  const innerShadowBoxShadow = useMemo(() => getInnerShadowOverlayStyle(effects), [effects]);

  const backgroundBlurOverlayStyle = useMemo(
    () => buildBgBlurOverlay(backgroundBlurPx),
    [backgroundBlurPx],
  );
  const innerShadowOverlayStyle = useMemo(
    () => buildInnerShadowOverlay(innerShadowBoxShadow),
    [innerShadowBoxShadow],
  );

  return {
    effectsStyle,
    hasBackgroundBlur: backgroundBlurPx > 0,
    hasInnerShadowOverlay: Boolean(innerShadowBoxShadow),
    backgroundBlurOverlayStyle,
    innerShadowOverlayStyle,
  };
};

// Bg-blur needs a stacking context on the Stack so its negative-z overlay stays
// scoped instead of escaping to an ancestor. Inner-shadow does not — keep
// absolute children free to escape the Stack's z-layer. Skip if the user has
// already positioned the Stack or set z-index (their layout intent wins).
export const applyEffectsStackingContext = (
  style: CSSProperties,
  hasBackgroundBlur: boolean,
  hasInnerShadowOverlay: boolean,
): CSSProperties => {
  const hasExplicitPosition = typeof style.position === 'string' && style.position !== 'static';
  if (hasBackgroundBlur) {
    return {
      ...style,
      ...(hasExplicitPosition ? null : { position: 'relative' }),
      ...(style.zIndex === undefined ? { zIndex: 0 } : null),
    };
  }
  if (hasInnerShadowOverlay && !hasExplicitPosition) {
    return { ...style, position: 'relative' };
  }
  return style;
};
